import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlusCircle, faList, faSave, faEraser, faTrash, faEdit, 
  faEye, faUserCircle, faCaretDown, faUser, faSyncAlt, 
  faSignOutAlt, faCrown, faCheckCircle, faExclamationCircle,
  faSpinner, faInfoCircle, faSignal, faNetworkWired, faWifi,
  faTimes, faMicrochip, faMemory, faHdd, faTachometerAlt,
  faClock, faCodeBranch, faDatabase, faEthernet
} from '@fortawesome/free-solid-svg-icons';
import '../scss/DeviceManagement.scss';
import BaseURL from '../assets/contants/BaseURL';

const DeviceManagement = () => {
  const navigate = useNavigate();
  
  // State variables
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [networkData, setNetworkData] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    deviceName: '',
    deviceToken: '',
    protocol: '',
    pubTopic: '',
    subTopic: '',
    hardwareVersion: '',
    softwareVersion: '',
    apiPath: '',
    deviceStatus: '1'
  });

  const wsRef = useRef(null);

  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');
  const getSessionID = () => sessionStorage.getItem('session_id') || localStorage.getItem('session_id');

  const getAuthHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    
    return headers;
  };

  useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        const token = getAuthToken();
        const organizationId = sessionStorage.getItem('organization_id');

        const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.logo_url) { 
            setOrgLogo(data.data.logo_url);
          }
        }
      } catch (error) {
        console.error('Error fetching org logo:', error);
      }
    };

    fetchOrgLogo();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const loadDevices = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BaseURL}meters/device/`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        showNotification('Session expired. Please login again.', 'error');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }
      
      if (!response.ok) throw new Error('Failed to fetch devices');
      
      const data = await response.json();
      setDevices(data);
    } catch (error) {
      console.error('Error fetching devices:', error);
      if (error.message !== 'Unauthorized') {
        showNotification('Error loading devices', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const fetchNetworkInterfaceData = (deviceToken, device) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
    }

    const token = getAuthToken();
    const session_id = getSessionID();

    if (!token || !session_id) {
        console.error('Missing authentication credentials for WebSocket');
        showNotification('Authentication failed. Please refresh the page.', 'error');
        return;
    }

    let host = BaseURL;
    if (BaseURL.includes('://')) {
        host = BaseURL.split('://')[1];
    }
    if (host.endsWith('/')) {
        host = host.slice(0, -1);
    }

    const wsScheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsScheme}//${host}/data/device-network/?token=${encodeURIComponent(token)}&session_id=${encodeURIComponent(session_id)}`;
    
    console.log('Connecting to WebSocket:', wsUrl.replace(token, '***'));
    
    const socket = new WebSocket(wsUrl);
    wsRef.current = socket;
    
    const connectionTimeout = setTimeout(() => {
        if (socket.readyState !== WebSocket.OPEN) {
            console.error('WebSocket connection timeout');
            socket.close();
            showNotification('Connection timeout. Please try again.', 'error');
        }
    }, 10000);
    
    socket.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket connected, requesting network data for device:', deviceToken);
        
        socket.send(JSON.stringify({
            type: 'get_network_interfaces',
            device_token: deviceToken
        }));
        
        socket.send(JSON.stringify({
            type: 'subscribe_device',
            device_token: deviceToken
        }));
    };
    
    socket.onmessage = (event) => {
        try {
            const response = JSON.parse(event.data);
            
            if (response.type === 'pong') {
                console.log('WebSocket pong received');
                return;
            }
            
            switch(response.type) {
                case 'network_interfaces':
                    if (response.data) {
                        setNetworkData(response.data);
                        console.log('Network data received:', response.data);
                    } else if (response.data === null) {
                        setNetworkData(null);
                        showNotification(response.message || 'No network data available', 'info');
                    }
                    break;
                case 'network_update':
                    if (response.data) {
                        setNetworkData(response.data);
                        showNotification('Network status updated', 'success');
                    }
                    break;
                case 'connection_established':
                    console.log('WebSocket connection established:', response.message);
                    break;
                case 'subscription_success':
                    console.log('Subscribed to device updates:', response.message);
                    break;
                case 'error':
                    console.error('WebSocket error:', response.message);
                    showNotification(`Error: ${response.message}`, 'error');
                    break;
                default:
                    console.log('Unknown message type:', response.type, response);
            }
        } catch (error) {
            console.error('Error parsing WebSocket message:', error);
        }
    };
    
    socket.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error('WebSocket error:', error);
        showNotification('Failed to connect to network service', 'error');
    };
    
    socket.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log(`WebSocket closed: Code=${event.code}, Reason=${event.reason}`);
        
        if (event.code !== 1000 && showModal) {
            console.log('Attempting to reconnect in 5 seconds...');
            setTimeout(() => {
                if (showModal && deviceToken) {
                    fetchNetworkInterfaceData(deviceToken, device);
                }
            }, 5000);
        }
    };
  };

  const getStatusBadge = (value) => {
    if (value === undefined || value === null) return 'N/A';
    if (value === 1 || value === true || value === '1' || value === 'active' || value === 'connected' || value === 'up') {
      return <span className="status-badge status-active">Connected/Active</span>;
    } else if (value === 0 || value === false || value === '0' || value === 'inactive' || value === 'disconnected' || value === 'down') {
      return <span className="status-badge status-inactive">Disconnected/Inactive</span>;
    }
    return value;
  };

  const formatValue = (value) => {
    if (value === undefined || value === null) return 'N/A';
    if (typeof value === 'number') return value.toFixed(2);
    return value;
  };

  const viewDeviceDetails = (device) => {
    setSelectedDevice(device);
    setNetworkData(null);
    setShowModal(true);
    
    if (device && device.device_token) {
      fetchNetworkInterfaceData(device.device_token, device);
    } else {
      showNotification('Device token not found', 'error');
    }
  };

  const closeModal = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    setShowModal(false);
    setSelectedDevice(null);
    setNetworkData(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const editDevice = (device) => {
    setFormData({
      deviceName: device.device_name || '',
      deviceToken: device.device_token || '',
      protocol: device.protocol || '',
      pubTopic: device.pub_topic || '',
      subTopic: device.sub_topic || '',
      hardwareVersion: device.hardware_version || '',
      softwareVersion: device.software_version || '',
      apiPath: device.api_path || '',
      deviceStatus: device.device_status?.toString() || '1'
    });
    setEditId(device.id);
    
    document.querySelector('.device-form-container')?.scrollIntoView({ behavior: 'smooth' });
  };

  const deleteDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to delete this device?')) return;
    
    try {
      const response = await fetch(`${BaseURL}meters/device/${deviceId}/`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        showNotification('Session expired. Please login again.', 'error');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }
      
      if (!response.ok) throw new Error('Failed to delete device');
      
      showNotification('Device deleted successfully', 'success');
      loadDevices();
      if (editId === deviceId) clearForm();
    } catch (error) {
      console.error('Error deleting device:', error);
      showNotification('Error deleting device', 'error');
    }
  };

  const clearForm = () => {
    setFormData({
      deviceName: '',
      deviceToken: '',
      protocol: '',
      pubTopic: '',
      subTopic: '',
      hardwareVersion: '',
      softwareVersion: '',
      apiPath: '',
      deviceStatus: '1'
    });
    setEditId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      device_name: formData.deviceName,
      device_token: formData.deviceToken,
      protocol: formData.protocol,
      pub_topic: formData.pubTopic,
      sub_topic: formData.subTopic,
      hardware_version: formData.hardwareVersion || null,
      software_version: formData.softwareVersion || null,
      api_path: formData.apiPath || null,
      device_status: parseInt(formData.deviceStatus)
    };
    
    const method = editId ? 'PUT' : 'POST';
    const url = editId ? `${BaseURL}meters/device/${editId}/` : `${BaseURL}meters/device/`;
    
    try {
      const response = await fetch(url, {
        method: method,
        headers: getAuthHeaders(),
        body: JSON.stringify(submitData)
      });
      
      if (response.status === 401) {
        showNotification('Session expired. Please login again.', 'error');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }
      
      if (!response.ok) throw new Error('Failed to save device');
      
      showNotification(
        editId ? 'Device updated successfully' : 'Device added successfully',
        'success'
      );
      loadDevices();
      clearForm();
    } catch (error) {
      console.error('Error saving device:', error);
      showNotification('Error saving device', 'error');
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      sessionStorage.clear();
      navigate('/login');
    }
  };

  useEffect(() => {
    loadDevices();
    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
  }, [loadDevices]);

  const filteredDevices = Array.isArray(devices) ? devices.filter(device => 
    !device.device_token || !device.device_token.toLowerCase().startsWith('remote_')
  ) : [];

  const isEditMode = !!editId;

  return (
    <div className="device-management-container">
      <div className="dashboard-content">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="header-logo">CraneIQ</div>
          </div>

          <div className="user-info-wrapper">
            <div className="account-dropdown">
              <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                {orgLogo ? (
                  <img
                    src={orgLogo}
                    alt="Organization Logo"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '2px solid #4361ee'
                    }}
                    onError={() => setOrgLogo(null)}
                  />
                ) : (
                  <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                )}
                <span>{sessionStorage.getItem('username') || 'User'}</span>
                <FontAwesomeIcon icon={faCaretDown} />
              </div>
              {isDropdownOpen && (
                <div className="account-menu show">
                  <div className="account-menu-item plan-info">
                    <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                    <span>Basic Plan</span>
                  </div>
                  <div className="account-menu-divider"></div>
                  <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faUser} /> Profile
                  </button>
                  <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                  </button>
                  <div className="account-menu-divider"></div>
                  <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page Title */}
        <div className="page-titles">
          <h1>Device Management</h1>
          <p>Manage all Device configurations and details</p>
        </div>

        {/* Device Form */}
        <div className="device-form-container">
          <h2 className="form-title">
            <FontAwesomeIcon icon={faPlusCircle} /> {isEditMode ? 'Edit Device' : 'Add Device'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Device Name</label>
                <input
                  type="text"
                  name="deviceName"
                  placeholder="eg: Crane Sensor 1"
                  value={formData.deviceName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Device Token</label>
                <input
                  type="text"
                  name="deviceToken"
                  placeholder="eg: 3489209"
                  value={formData.deviceToken}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Protocol</label>
                <select name="protocol" className="filter-select" value={formData.protocol} onChange={handleInputChange} required>
                  <option value="">Select Protocol</option>
                  <option value="mqtt">MQTT</option>
                  <option value="http">HTTP</option>
                </select>
              </div>
              <div className="form-group">
                <label>Pub Topic</label>
                <input
                  type="text"
                  name="pubTopic"
                  placeholder="eg: crane/sensor/data"
                  value={formData.pubTopic}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Sub Topic</label>
                <input
                  type="text"
                  name="subTopic"
                  placeholder="eg: /status"
                  value={formData.subTopic}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Hardware Version</label>
                <input
                  type="text"
                  name="hardwareVersion"
                  placeholder="eg: 2.3.0"
                  value={formData.hardwareVersion}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Software Version</label>
                <input
                  type="text"
                  name="softwareVersion"
                  placeholder="eg: 1.2.0"
                  value={formData.softwareVersion}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>API Path</label>
                <input
                  type="text"
                  name="apiPath"
                  placeholder="API Path"
                  value={formData.apiPath}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Device Status</label>
                <select name="deviceStatus" className="filter-select" value={formData.deviceStatus} onChange={handleInputChange} required>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                <FontAwesomeIcon icon={faSave} /> {isEditMode ? 'Update Device' : 'Add Device'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={clearForm}>
                <FontAwesomeIcon icon={faEraser} /> Clear
              </button>
              {isEditMode && (
                <button type="button" className="btn btn-danger" onClick={() => deleteDevice(editId)}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Device Table */}
        <div className="device-table-container">
          <h2 className="form-title">
            <FontAwesomeIcon icon={faList} /> Device List
          </h2>

          {isLoading ? (
            <div className="loading-spinner-container">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: 'var(--primary-color)' }} />
              <p>Loading devices...</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="device-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Device Name</th>
                    <th>Device Token</th>
                    <th>Protocol</th>
                    <th>Pub Topic</th>
                    <th>Sub Topic</th>
                    <th>HW Ver.</th>
                    <th>SW Ver.</th>
                    <th>API Path</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.length === 0 ? (
                    <tr>
                      <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#95a5a6' }}>
                        No devices found
                      </td>
                    </tr>
                  ) : (
                    filteredDevices.map((device, index) => (
                      <tr key={device.id}>
                        <td>{index + 1}</td>
                        <td>{device.device_name || '-'}</td>
                        <td>{device.device_token || '-'}</td>
                        <td>{device.protocol ? device.protocol.toUpperCase() : '-'}</td>
                        <td>{device.pub_topic || '-'}</td>
                        <td>{device.sub_topic || '-'}</td>
                        <td>{device.hardware_version || '-'}</td>
                        <td>{device.software_version || '-'}</td>
                        <td>{device.api_path || '-'}</td>
                        <td className={device.device_status === 1 ? 'status-active' : 'status-inactive'}>
                          {device.device_status == 1 ? 'Active' : 'Inactive'}
                        </td>
                        <td className="action-btns">
                          <button className="view-btns" onClick={() => viewDeviceDetails(device)}>
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                          <button className="edit-btn" onClick={() => editDevice(device)}>
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button className="delete-btn" onClick={() => deleteDevice(device.id)}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Device Details Modal - Updated to show ALL fields from PDF */}
      {showModal && (
        <div className="modals" onClick={closeModal}>
          <div className="modals-content modals-content-large" onClick={(e) => e.stopPropagation()}>
            <div className="modals-header">
              <h3>
                <FontAwesomeIcon icon={faInfoCircle} /> Device Details — {selectedDevice?.device_name || 'Unknown Device'}
              </h3>
              <span className="close-modal" onClick={closeModal}>
                <FontAwesomeIcon icon={faTimes} />
              </span>
            </div>
            <div className="modals-body">

              {/* Section 1: System Information */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faCodeBranch} /> System Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Device Hostname:</span>
                    <span className="detail-value">{networkData?.system?.hostname || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">OS Version:</span>
                    <span className="detail-value">{networkData?.system?.os_version || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Kernel Version:</span>
                    <span className="detail-value">{networkData?.system?.kernel_version || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">System Architecture:</span>
                    <span className="detail-value">{networkData?.system?.architecture || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label"><FontAwesomeIcon icon={faClock} /> System Uptime:</span>
                    <span className="detail-value">{networkData?.system?.uptime || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Current Date/Timezone:</span>
                    <span className="detail-value">{networkData?.system?.timezone || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Firmware Version:</span>
                    <span className="detail-value">{networkData?.system?.firmware_version || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Section 2: CPU Information */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faMicrochip} /> CPU Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Processor Model:</span>
                    <span className="detail-value">{networkData?.cpu?.processor_model || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">CPU Cores:</span>
                    <span className="detail-value">{networkData?.cpu?.cores || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Clock Frequency:</span>
                    <span className="detail-value">{networkData?.cpu?.clock_frequency_mhz ? `${networkData.cpu.clock_frequency_mhz} MHz` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Load Average:</span>
                    <span className="detail-value">{formatValue(networkData?.cpu?.load_average)}</span>
                  </div>
                </div>
              </div>

              {/* Section 3: Memory Information */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faMemory} /> Memory Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Total RAM:</span>
                    <span className="detail-value">{networkData?.memory?.total_ram_mb ? `${formatValue(networkData.memory.total_ram_mb)} MB` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Used RAM:</span>
                    <span className="detail-value">{networkData?.memory?.used_ram_mb ? `${formatValue(networkData.memory.used_ram_mb)} MB` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Free RAM:</span>
                    <span className="detail-value">{networkData?.memory?.free_ram_mb ? `${formatValue(networkData.memory.free_ram_mb)} MB` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Available RAM:</span>
                    <span className="detail-value">{networkData?.memory?.available_ram_mb ? `${formatValue(networkData.memory.available_ram_mb)} MB` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Buffers & Cache:</span>
                    <span className="detail-value">{networkData?.memory?.buffers_cache_mb ? `${formatValue(networkData.memory.buffers_cache_mb)} MB` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Swap Memory:</span>
                    <span className="detail-value">{networkData?.memory?.swap_memory_mb ? `${formatValue(networkData.memory.swap_memory_mb)} MB` : 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Section 4: Storage Information */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faHdd} /> Storage Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Total Storage:</span>
                    <span className="detail-value">{networkData?.storage?.total_capacity_mb ? `${formatValue(networkData.storage.total_capacity_mb)} MB` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Used Storage:</span>
                    <span className="detail-value">{networkData?.storage?.used_space_mb ? `${formatValue(networkData.storage.used_space_mb)} MB` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Available Storage:</span>
                    <span className="detail-value">{networkData?.storage?.available_space_mb ? `${formatValue(networkData.storage.available_space_mb)} MB` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Storage Usage:</span>
                    <span className="detail-value">{networkData?.storage?.usage_percentage ? `${formatValue(networkData.storage.usage_percentage)}%` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Mount Points:</span>
                    <span className="detail-value">{networkData?.storage?.mount_points || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Flash Partition Layout:</span>
                    <span className="detail-value">{networkData?.storage?.flash_partition_layout || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Section 5: CPU Usage - Separate section */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faTachometerAlt} /> CPU Usage</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Total CPU Usage:</span>
                    <span className="detail-value">{networkData?.cpu?.total_usage_percent ? `${formatValue(networkData.cpu.total_usage_percent)}%` : 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Section 6: Network Status - LAN (Ethernet) */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faNetworkWired} /> Ethernet Interfaces (LAN)</h4>
                
                {/* ETH0 */}
                <div className="sub-section">
                  <h5>ETH0</h5>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">MAC Address:</span>
                      <span className="detail-value">{networkData?.ethernet?.eth0?.mac_address || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">IP Address:</span>
                      <span className="detail-value">{networkData?.ethernet?.eth0?.ip_address || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Connection State:</span>
                      <span className="detail-value">{getStatusBadge(networkData?.ethernet?.eth0?.state)}</span>
                    </div>
                  </div>
                </div>

                {/* ETH1 */}
                <div className="sub-section">
                  <h5>ETH1</h5>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">MAC Address:</span>
                      <span className="detail-value">{networkData?.ethernet?.eth1?.mac_address || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">IP Address:</span>
                      <span className="detail-value">{networkData?.ethernet?.eth1?.ip_address || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Connection State:</span>
                      <span className="detail-value">{getStatusBadge(networkData?.ethernet?.eth1?.state)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 7: Network Status - WLAN (WiFi) */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faWifi} /> Wireless LAN (WiFi)</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">MAC Address:</span>
                    <span className="detail-value">{networkData?.wifi?.mac_address || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">SSID:</span>
                    <span className="detail-value">{networkData?.wifi?.ssid || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">BSSID:</span>
                    <span className="detail-value">{networkData?.wifi?.bssid || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">IP Address:</span>
                    <span className="detail-value">{networkData?.wifi?.ip_address || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Frequency:</span>
                    <span className="detail-value">{networkData?.wifi?.frequency_mhz ? `${networkData.wifi.frequency_mhz} MHz` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Connection State:</span>
                    <span className="detail-value">{getStatusBadge(networkData?.wifi?.state)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Signal Strength:</span>
                    <span className="detail-value">{networkData?.wifi?.signal_strength || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Section 8: Network Status - LTE (Cellular) */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faSignal} /> Cellular (LTE)</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">IMEI:</span>
                    <span className="detail-value">{networkData?.cellular?.imei || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">IMSI:</span>
                    <span className="detail-value">{networkData?.cellular?.imsi || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">ICCID:</span>
                    <span className="detail-value">{networkData?.cellular?.iccid || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">IP Address:</span>
                    <span className="detail-value">{networkData?.cellular?.ip_address || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Operator Name:</span>
                    <span className="detail-value">{networkData?.cellular?.operator_name || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Operator ID:</span>
                    <span className="detail-value">{networkData?.cellular?.operator_id || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Network Technology:</span>
                    <span className="detail-value">{networkData?.cellular?.technology || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Signal Strength:</span>
                    <span className="detail-value">{networkData?.cellular?.signal_strength_percent ? `${formatValue(networkData.cellular.signal_strength_percent)}%` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Transmit Power:</span>
                    <span className="detail-value">{networkData?.cellular?.transmit_power_level ? `${formatValue(networkData.cellular.transmit_power_level)} dBm` : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Connection State:</span>
                    <span className="detail-value">{getStatusBadge(networkData?.cellular?.state)}</span>
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="detail-section">
                <h4><FontAwesomeIcon icon={faClock} /> Timestamps</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Created At:</span>
                    <span className="detail-value">{networkData?.created_at ? new Date(networkData.created_at).toLocaleString() : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Last Updated:</span>
                    <span className="detail-value">{networkData?.updated_at ? new Date(networkData.updated_at).toLocaleString() : 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Summary */}
              {networkData?.summary && (
                <div className="detail-section">
                  <h4><FontAwesomeIcon icon={faDatabase} /> Summary</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span className="detail-value">{getStatusBadge(networkData.summary.status)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Active Interfaces:</span>
                      <span className="detail-value">{networkData.summary.active_interfaces_count || 0}</span>
                    </div>
                    <div className="detail-item full-width">
                      <span className="detail-label">Details:</span>
                      <div className="detail-value">
                        {networkData.summary.details?.map((detail, idx) => (
                          <div key={idx}>{detail}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modals-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type} show`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheckCircle : faExclamationCircle} />
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default DeviceManagement;